/**
 * TEMPORARY — sc-624 parity admin tool.
 * Delete after typed-tables migration cutover.
 */

import * as React from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  Copy,
  RefreshCw,
} from 'lucide-react'
import { toast } from 'sonner'
import { ParityStatusBadge } from '../-components/ParityStatusBadge'
import type {
  ParityReviewStatus,
  ParitySectionResult,
} from '@/types/parity.types'
import { PageTitle } from '@/components/PageTitle'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/animate-ui/primitives/radix/collapsible'
import {
  useParityDetail,
  useParityPdfBlob,
  useReportBucketPaths,
  useUpdateParityReview,
} from '@/hooks/repositories/useParityRepository'
import { useGenerateRothologyReport } from '@/hooks/repositories/useForecastsRepository'
import { cn } from '@/lib/utils'
import { requirePermission } from '@/lib/permissions/route-guards'
import { BACKOFFICE_PERMISSION_KEYS } from '@/lib/permissions/backoffice-permissions'

export const Route = createFileRoute(
  '/_app/backoffice/admin/parity/$forecastId/',
)({
  component: RouteComponent,
  beforeLoad: requirePermission(BACKOFFICE_PERMISSION_KEYS.devTesting),
})

// ---------------------------------------------------------------------------
// Static page keys (reference pages that are not data-comparable)
// ---------------------------------------------------------------------------

const STATIC_PAGE_KEYS = ['coverPage', 'tocPage', 'termsToKnowPage'] as const

// ---------------------------------------------------------------------------
// Section diff
// ---------------------------------------------------------------------------

function formatValue(v: unknown): string {
  if (v === undefined) return '(missing)'
  if (v === null) return 'null'
  if (typeof v === 'object') return JSON.stringify(v, null, 2)
  return String(v)
}

interface SectionDiffProps {
  section: ParitySectionResult
  defaultOpen: boolean
}

function SectionDiff({ section, defaultOpen }: SectionDiffProps) {
  const diffCount = section.fields.filter((f) => !f.equal).length
  return (
    <Collapsible defaultOpen={defaultOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-muted/60',
            section.ok ? 'text-foreground' : 'text-red-700 dark:text-red-400',
          )}
        >
          <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 [[data-state=closed]_&]:-rotate-90" />
          <span className="font-mono">{section.page}</span>
          {!section.ok && (
            <span className="ml-auto text-xs text-red-600 dark:text-red-400">
              {diffCount} diff{diffCount !== 1 ? 's' : ''}
            </span>
          )}
          {section.ok && (
            <span className="ml-auto text-xs text-green-600 dark:text-green-400">
              ok
            </span>
          )}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-1 mb-3 ml-4 rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40%]">Field</TableHead>
                <TableHead className="w-[30%]">JSON value</TableHead>
                <TableHead className="w-[30%]">Tables value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {section.fields.map((field, i) => (
                <TableRow
                  key={i}
                  className={
                    !field.equal ? 'bg-red-50 dark:bg-red-950/30' : undefined
                  }
                >
                  <TableCell className="font-mono text-xs break-all">
                    <span
                      className={cn(
                        !field.equal &&
                          'text-red-700 dark:text-red-400 font-semibold',
                      )}
                    >
                      {field.key || '(root)'}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-xs whitespace-pre-wrap break-all">
                    <span
                      className={
                        !field.equal
                          ? 'text-red-700 dark:text-red-400'
                          : 'text-green-700 dark:text-green-400'
                      }
                    >
                      {formatValue(field.json)}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-xs whitespace-pre-wrap break-all">
                    <span
                      className={
                        !field.equal
                          ? 'text-red-700 dark:text-red-400'
                          : 'text-green-700 dark:text-green-400'
                      }
                    >
                      {formatValue(field.tables)}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
              {section.fields.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    className="text-muted-foreground text-center py-4 text-sm"
                  >
                    No fields compared
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

// ---------------------------------------------------------------------------
// PDF pane
// ---------------------------------------------------------------------------

interface PdfPaneProps {
  forecastId: string
  label: string
  variant: 'json' | 'tables'
  showRegenerate?: boolean
  bucketPath?: string | null
}

function PdfPane({
  forecastId,
  label,
  variant,
  showRegenerate,
  bucketPath,
}: PdfPaneProps) {
  const { blobUrl, loading, error } = useParityPdfBlob(forecastId, variant)
  const regenerate = useGenerateRothologyReport()

  const handleRegenerate = React.useCallback(() => {
    regenerate.mutate(forecastId, {
      onSuccess: () =>
        toast.success('Report generation queued. Reload in a few minutes.'),
      onError: () => toast.error('Failed to enqueue report generation'),
    })
  }, [forecastId, regenerate])

  const handleCopyPath = React.useCallback(() => {
    if (!bucketPath) return
    void navigator.clipboard.writeText(bucketPath).then(() => {
      toast.success('Cloud Console link copied')
    })
  }, [bucketPath])

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-muted-foreground">{label}</div>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 gap-1.5 text-xs"
          disabled={!bucketPath}
          title={bucketPath ?? 'Path unavailable'}
          onClick={handleCopyPath}
        >
          <Copy className="h-3.5 w-3.5" />
          Copy path
        </Button>
      </div>
      <div className="border rounded-md bg-muted/20 min-h-[600px] flex flex-col items-center justify-center overflow-hidden">
        {loading && (
          <div className="flex flex-col items-center gap-2 p-8">
            <Skeleton className="h-full w-full min-h-[560px] rounded-md" />
          </div>
        )}
        {!loading && error && (
          <div className="flex flex-col items-center gap-3 p-8 text-center">
            <AlertCircle className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {error.message.includes('404') ||
              (error as { status?: number }).status === 404
                ? 'PDF not available — this forecast may predate typed tables.'
                : 'Failed to load PDF.'}
            </p>
            {showRegenerate && (
              <Button
                size="sm"
                variant="outline"
                disabled={regenerate.isPending}
                onClick={handleRegenerate}
              >
                {regenerate.isPending ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Regenerate
              </Button>
            )}
          </div>
        )}
        {!loading && blobUrl && (
          <iframe
            src={blobUrl}
            className="w-full"
            style={{ height: '700px', border: 'none' }}
            title={label}
          />
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Verdict controls
// ---------------------------------------------------------------------------

interface VerdictControlsProps {
  forecastId: string
  currentStatus: ParityReviewStatus
  currentNotes: string | null
  reviewedAt: string | null
  reviewedById: string | null
}

function VerdictControls({
  forecastId,
  currentStatus,
  currentNotes,
  reviewedAt,
}: VerdictControlsProps) {
  const [notes, setNotes] = React.useState(currentNotes ?? '')
  const updateReview = useUpdateParityReview(forecastId)

  // Sync notes if currentNotes changes (e.g. after invalidation)
  React.useEffect(() => {
    setNotes(currentNotes ?? '')
  }, [currentNotes])

  const submit = (status: ParityReviewStatus) => {
    updateReview.mutate({ status, notes: notes || undefined })
  }

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium">Current verdict:</span>
        <ParityStatusBadge status={currentStatus} />
        {reviewedAt && (
          <span className="text-xs text-muted-foreground ml-auto">
            Last reviewed {new Date(reviewedAt).toLocaleString()}
          </span>
        )}
      </div>

      <Textarea
        placeholder="Optional notes..."
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        className="resize-none text-sm"
        rows={3}
      />

      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5 border-green-600 text-green-700 hover:bg-green-50 dark:hover:bg-green-950"
          disabled={updateReview.isPending}
          onClick={() => submit('VERIFIED')}
        >
          <CheckCircle2 className="h-4 w-4" />
          Mark Verified
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5 border-red-600 text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
          disabled={updateReview.isPending}
          onClick={() => submit('FLAGGED')}
        >
          <AlertCircle className="h-4 w-4" />
          Flag as not good
        </Button>
        {currentStatus !== 'PENDING' && (
          <Button
            size="sm"
            variant="ghost"
            className="text-muted-foreground"
            disabled={updateReview.isPending}
            onClick={() => submit('PENDING')}
          >
            Reset to Pending
          </Button>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Route component
// ---------------------------------------------------------------------------

function RouteComponent() {
  const { forecastId } = Route.useParams()
  const { data, isLoading, error } = useParityDetail(forecastId)
  // Hooks must run unconditionally — declare before any early return.
  const [hideStatic, setHideStatic] = React.useState(true)
  const { data: bucketPaths } = useReportBucketPaths(forecastId)

  if (isLoading) {
    return (
      <div className="py-4 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="py-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load parity detail. The forecast may not exist.
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  const { parity, review } = data

  const visibleSections = hideStatic
    ? parity.sections.filter(
        (s) => !(STATIC_PAGE_KEYS as ReadonlyArray<string>).includes(s.page),
      )
    : parity.sections

  const hiddenStaticCount = parity.sections.length - visibleSections.length

  // Compute summary counts from visible sections
  const diffSectionCount = visibleSections.filter((s) => !s.ok).length
  const diffFieldCount = visibleSections.reduce(
    (acc, s) => acc + s.fields.filter((f) => !f.equal).length,
    0,
  )

  return (
    <div className="py-4 space-y-4">
      <PageTitle title="Parity Detail (temp)" />

      {/* Back link */}
      <div>
        <Button variant="ghost" size="sm" asChild className="gap-1 -ml-2">
          <Link to="/backoffice/admin/parity">
            <ChevronLeft className="h-4 w-4" />
            Back to list
          </Link>
        </Button>
      </div>

      {/* 1 — Summary banner */}
      {!parity.generatedJson || !parity.generatedTables ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {parity.error ?? 'One or both report paths failed to generate.'}
            {!parity.generatedTables && (
              <span className="block mt-1 text-xs opacity-80">
                Tables path failed — this forecast may predate typed tables.
              </span>
            )}
          </AlertDescription>
        </Alert>
      ) : parity.ok ? (
        <Alert className="border-green-600 bg-green-50 text-green-800 dark:bg-green-950 dark:text-green-300">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription>
            Parity — all fields match across {parity.sections.length} sections.
          </AlertDescription>
        </Alert>
      ) : (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {diffFieldCount} field{diffFieldCount !== 1 ? 's' : ''} differ
            across {diffSectionCount} section{diffSectionCount !== 1 ? 's' : ''}
            .
            {hideStatic && hiddenStaticCount > 0 && (
              <span className="ml-1 opacity-75">
                ({hiddenStaticCount} static page
                {hiddenStaticCount !== 1 ? 's' : ''} hidden)
              </span>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* 2 — Section diffs */}
      {parity.sections.length > 0 && (
        <div className="rounded-lg border bg-card p-4 space-y-1">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold">Data diff by section</h2>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <Switch
                checked={hideStatic}
                onCheckedChange={setHideStatic}
                id="hide-static-toggle"
              />
              <span className="text-xs text-muted-foreground">
                Hide static pages (cover, TOC, terms-to-know)
              </span>
            </label>
          </div>
          {visibleSections.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">
              No non-static sections to compare.
            </p>
          ) : (
            visibleSections.map((section) => (
              <SectionDiff
                key={section.page}
                section={section}
                defaultOpen={!section.ok}
              />
            ))
          )}
        </div>
      )}

      {/* 3 — Side-by-side PDF view */}
      <div className="rounded-lg border bg-card p-4">
        <h2 className="text-sm font-semibold mb-3">PDF comparison</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <PdfPane
            forecastId={forecastId}
            label="JSON report path"
            variant="json"
            bucketPath={bucketPaths?.json}
          />
          <PdfPane
            forecastId={forecastId}
            label="Typed-tables report path"
            variant="tables"
            showRegenerate
            bucketPath={bucketPaths?.typedTables}
          />
        </div>
      </div>

      {/* 4 — Verdict controls */}
      <VerdictControls
        forecastId={forecastId}
        currentStatus={review.status}
        currentNotes={review.notes}
        reviewedAt={review.reviewedAt}
        reviewedById={review.reviewedById}
      />
    </div>
  )
}
